import { useState } from 'react';
import { devApi, uploadAttachment } from '../api.js';
import { TYPE_LABEL } from '../format.js';
import AttachmentPicker from './AttachmentPicker.jsx';
import MarkdownEditor from './MarkdownEditor.jsx';

const EMPTY = {
  title: '', description: '', dev_notes: '', target_module: '', area: '',
  task_type: 'bug', status: 'defined', execution_mode: 'manual', section_id: '',
};

// The module a new task defaults to: whichever one the last new task was filed against. Work
// arrives in runs — five things wrong with the same module in one sitting — and re-picking it
// five times is the cost of a default that is always the first module in the list.
//
// Local to the browser on purpose. It is a convenience about how someone is working this
// afternoon, not a fact about the task, and it must never be what a task ends up saved with
// unless it is also what the field is showing.
const LAST_MODULE_KEY = 'orbitdev:last-target-module';

function readLastModule(options) {
  try {
    const id = localStorage.getItem(LAST_MODULE_KEY);
    // Only if it is still a module that exists — the registry changes, and a stale id would
    // leave the select showing nothing while quietly holding a value.
    return options?.modules?.some(m => m.id === id) ? id : '';
  } catch { return ''; }
}

function rememberLastModule(id) {
  try { if (id) localStorage.setItem(LAST_MODULE_KEY, id); } catch { /* private mode */ }
}

function Segmented({ label, value, options, labels, onChange, help }) {
  return (
    <div>
      <span className="o-field-lbl">{label}</span>
      <div className="o-toggle">
        {options.map(opt => (
          <button key={opt} type="button" aria-selected={value === opt}
            onClick={() => onChange(opt)}>{labels[opt] || opt}</button>
        ))}
      </div>
      {help && <p className="o-note" style={{ marginTop: 6, marginBottom: 0 }}>{help}</p>}
    </div>
  );
}

/**
 * Create or edit a task.
 *
 * `task` absent means create. Attachments differ between the two: an existing task can take an
 * upload straight away, a new one has nowhere to put it yet, so they are held and uploaded
 * after the task is saved and has an id.
 *
 * Laid out as two columns across the full page width rather than one narrow one: writing the
 * task is the left column, classifying it is the right, and neither should be found by
 * scrolling past the other.
 *
 * Two things the task still carries are deliberately not editable here. The **section** is
 * decided by where the task was created and by dragging it in the list, which is the place the
 * arrangement is actually visible; a second control for it inside the task let the two disagree.
 * **Execution mode** belongs to running the work, not to describing it, and that is a separate
 * module — the form still round-trips both so no record is lost.
 */
export default function TaskForm({
  task, options, sectionId = null, attachments = [],
  onSaved, onCancel, onDelete, headerActions = null,
}) {
  const isNew = !task;
  const [form, setForm] = useState(() => ({
    ...EMPTY,
    ...(task || {}),
    dev_notes: task?.dev_notes || '',
    area: task?.area || '',
    // `sectionId` is the section this form was opened from, so a task started inside a section
    // is filed there without a second step.
    section_id: task?.section_id || sectionId || '',
    // An existing task shows its own module, always. The remembered one is only ever a starting
    // point for a task that does not have one yet.
    target_module: task?.target_module || readLastModule(options) || options?.modules?.[0]?.id || '',
  }));
  const [shots, setShots] = useState(attachments);
  const [pending, setPending] = useState([]);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [err, setErr] = useState(null);

  const set = (key) => (value) => setForm(f => ({ ...f, [key]: value }));

  const save = async () => {
    if (!form.title.trim()) return;
    setSaving(true); setErr(null);
    try {
      // `files` is deliberately absent: the form no longer collects it, and sending an empty
      // list would erase whatever an older task already has.
      const body = {
        title: form.title.trim(),
        description: form.description,
        dev_notes: form.dev_notes,
        target_module: form.target_module,
        area: form.area,
        task_type: form.task_type,
        status: form.status,
        execution_mode: form.execution_mode,
        section_id: form.section_id || null,
      };
      const saved = isNew
        ? (await devApi.createTask(body)).task
        : (await devApi.updateTask(task.id, body)).task;

      // Only a created task moves the default. Correcting the module on an old task is a fix to
      // that task, not a statement about what you are about to write next.
      if (isNew) rememberLastModule(body.target_module);

      // Held pastes go up now that there is an id to hang them on. One failure must not lose
      // the task that was just written, so the error is surfaced and the save still counts.
      if (pending.length) {
        try {
          for (const p of pending) await uploadAttachment(saved.id, p.file);
          pending.forEach(p => p.url && URL.revokeObjectURL(p.url));
          setPending([]);
        } catch (e) {
          setErr(`Task saved, but an attachment did not upload: ${e.message}`);
        }
      }

      onSaved(saved);
    } catch (e) {
      setErr(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="o-panel" style={{ padding: 24 }}>
      <div className="o-head-row" style={{ marginBottom: 20 }}>
        <div style={{ minWidth: 0 }}>
          <h1 className="o-title" style={{ fontSize: '1.25rem' }}>{isNew ? 'New task' : 'Edit task'}</h1>
          <p className="o-sub" style={{ margin: 0 }}>
            Write enough that the task can be picked up later without this conversation.
          </p>
        </div>
        {headerActions}
      </div>

      <div className="o-form-split">
        <div>
          <label className="o-field-lbl" htmlFor="tf-title">Title</label>
          <input id="tf-title" className="o-control" autoFocus value={form.title}
            placeholder="e.g. Workspace selector flickers on first load"
            onChange={e => set('title')(e.target.value)} />

          {/* Directly under the title, because it is part of naming the task: what it is called
              and what it is against are read together. */}
          <div style={{ marginTop: 16 }}>
            <label className="o-field-lbl" htmlFor="tf-module">Target module</label>
            <select id="tf-module" className="o-control" value={form.target_module}
              onChange={e => set('target_module')(e.target.value)}>
              {(options?.modules || []).map(m => (
                <option key={m.id} value={m.id}>{m.label}</option>
              ))}
            </select>
          </div>

          <div style={{ marginTop: 16 }}>
            <label className="o-field-lbl" htmlFor="tf-desc">Description</label>
            {/* Height from the viewport rather than a row count: the point of the two columns is
                that the whole form is on one screen, and a fixed 10 rows is what pushed the
                options below the fold. Still drag-resizable when a long description needs it. */}
            <MarkdownEditor id="tf-desc" value={form.description} onChange={set('description')}
              placeholder="What is wrong or what should exist, where it happens, and how to tell when it is done."
              height="clamp(104px, 16vh, 240px)" />
          </div>

          <div style={{ marginTop: 16 }}>
            <label className="o-field-lbl" htmlFor="tf-notes">Development notes</label>
            <MarkdownEditor id="tf-notes" value={form.dev_notes} onChange={set('dev_notes')}
              placeholder="Constraints, what was already tried, approaches that are ruled out."
              height="clamp(76px, 10vh, 160px)" minHeight={76} />
            <p className="o-note" style={{ marginTop: 6, marginBottom: 0 }}>
              Context around the work rather than the work itself. The description stays the ask.
            </p>
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <label className="o-field-lbl" htmlFor="tf-area">Page or area <span style={{ textTransform: 'none', letterSpacing: 0 }}>(optional)</span></label>
            <input id="tf-area" className="o-control" value={form.area}
              placeholder="e.g. Campaigns list, top bar"
              onChange={e => set('area')(e.target.value)} />
          </div>

          <Segmented label="Type" value={form.task_type} options={options?.types || []}
            labels={TYPE_LABEL} onChange={set('task_type')} />

          {/* No Status control. A task's status is moved through the buttons in the strip
              above this form, which is what leaves a row in the transition log — a segmented
              control here would be a second way to change it that records nothing, and the
              legacy `status` column it used to write is unread since migration 915. */}

          <div>
            <span className="o-field-lbl">Attachments</span>
            <AttachmentPicker
              taskId={task?.id || null}
              saved={shots} onSavedChange={setShots}
              pending={pending} onPendingChange={setPending}
            />
          </div>
        </div>
      </div>

      {err && <p className="o-state o-error" style={{ padding: '12px 0', textAlign: 'left' }}>{err}</p>}

      {/* Delete sits with the other footer actions but pushed to the far end, away from Save.
          It only exists for a task that has already been written — there is nothing to delete
          before the first save, and Cancel is what abandons a new one. */}
      <div style={{ display: 'flex', gap: 8, marginTop: 20, alignItems: 'center' }}>
        <button className="o-btn o-btn-primary" onClick={save} disabled={saving || !form.title.trim()}>
          {saving ? 'Saving…' : isNew ? 'Create task' : 'Save changes'}
        </button>
        <button className="o-btn o-btn-ghost" onClick={onCancel} disabled={saving}>Cancel</button>

        {!isNew && onDelete && (
          <span style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
            {confirmDelete ? (
              <>
                <span className="o-note" style={{ margin: 0 }}>
                  Its attachments go with it. This cannot be undone.
                </span>
                <button className="o-btn o-btn-danger o-btn-sm" disabled={saving}
                  onClick={() => onDelete()}>Delete task</button>
                <button className="o-btn o-btn-ghost o-btn-sm"
                  onClick={() => setConfirmDelete(false)}>Keep it</button>
              </>
            ) : (
              <button className="o-btn o-btn-ghost o-btn-sm" disabled={saving}
                onClick={() => setConfirmDelete(true)}>Delete task</button>
            )}
          </span>
        )}
      </div>
    </div>
  );
}
